import { useAuth } from '@/features/auth/useAuth';
import { USER_ROLE_LABELS } from '@coreltb/shared/constants';
import { Search, X } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { NavSheetList } from './NavList';
import { initialsOf } from './UserMenu';

/**
 * Menu na < lg: panel wysuwany z dolu z lista w grupach (Dawid 2026-09-30 —
 * wariant A „lista z dolu”). Prawie caly ekran, wyszukiwarka pozycji.
 * Zamyka: zmiana adresu, ESC, klik w tlo, X, przeciagniecie w dol.
 */
export function MobileSidebar({ open, onClose }: { open: boolean; onClose: () => void }) {
  const { user } = useAuth();
  const location = useLocation();
  const [dragY, setDragY] = useState(0);
  const [query, setQuery] = useState('');
  const startY = useRef<number | null>(null);

  // biome-ignore lint/correctness/useExhaustiveDependencies: zamykamy tylko przy zmianie adresu
  useEffect(() => {
    if (open) onClose();
  }, [location.pathname]);

  useEffect(() => {
    if (!open) return;
    setDragY(0);
    setQuery('');
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handler);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', handler);
      document.body.style.overflow = '';
    };
  }, [open, onClose]);

  if (!open) return null;

  const onTouchStart = (e: React.TouchEvent) => {
    startY.current = e.touches[0]?.clientY ?? null;
  };
  const onTouchMove = (e: React.TouchEvent) => {
    if (startY.current === null) return;
    const dy = (e.touches[0]?.clientY ?? 0) - startY.current;
    setDragY(Math.max(0, dy));
  };
  const onTouchEnd = () => {
    if (dragY > 90) onClose();
    else setDragY(0);
    startY.current = null;
  };

  return (
    <div className="fixed inset-0 z-50 lg:hidden">
      <div
        className="absolute inset-0 animate-fade-in bg-black/50"
        onClick={onClose}
        role="presentation"
        aria-hidden="true"
      />
      <div
        role="dialog"
        aria-modal="true"
        aria-label="Menu"
        style={{ transform: dragY ? `translateY(${dragY}px)` : undefined }}
        className="absolute inset-x-0 bottom-0 flex max-h-[90dvh] animate-slide-up flex-col rounded-t-xl border-t border-border bg-bg shadow-overlay transition-transform duration-150"
      >
        {/* Uchwyt + profil — przeciagniecie w dol zamyka */}
        <div
          className="shrink-0 touch-none px-4 pt-2.5"
          onTouchStart={onTouchStart}
          onTouchMove={onTouchMove}
          onTouchEnd={onTouchEnd}
        >
          <div className="mx-auto mb-3 h-1 w-10 rounded-full bg-border-strong" aria-hidden />
          <div className="flex items-center justify-between gap-3">
            {user && (
              <div className="flex min-w-0 items-center gap-3">
                <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-accent-subtle text-sm font-semibold text-accent ring-1 ring-accent/30">
                  {initialsOf(user.name)}
                </div>
                <div className="min-w-0">
                  <div className="truncate text-base font-semibold text-fg">{user.name}</div>
                  <div className="truncate text-xs text-fg-muted">
                    {USER_ROLE_LABELS[user.role] ?? user.role}
                  </div>
                </div>
              </div>
            )}
            <button
              type="button"
              onClick={onClose}
              aria-label="Zamknij menu"
              className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-surface-2 text-fg-muted hover:text-fg"
            >
              <X className="h-5 w-5" strokeWidth={2} />
            </button>
          </div>
        </div>
        <div className="shrink-0 px-4 pb-3 pt-3">
          <label className="flex h-11 items-center gap-2 rounded-lg border border-border bg-surface px-3 text-fg-muted focus-within:border-accent focus-within:ring-2 focus-within:ring-accent-ring">
            <Search className="h-4 w-4 shrink-0" strokeWidth={2} />
            <input
              type="search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Szukaj w menu"
              aria-label="Szukaj w menu"
              className="min-w-0 flex-1 border-0 !bg-transparent p-0 text-base !shadow-none text-fg placeholder:text-fg-subtle focus:outline-none focus:ring-0"
            />
          </label>
        </div>
        <div className="flex-1 overflow-y-auto overscroll-contain px-4 pb-[calc(1.25rem+env(safe-area-inset-bottom))]">
          <NavSheetList query={query} />
        </div>
      </div>
    </div>
  );
}
